'use client';

import { canUseDOM } from 'utils/can-use-dom';
import { noop } from 'utils/noop';
import type { AnimationKeyframeWithOptions } from './hooks/types';
import { applyDefaultOptions } from './hooks/utils/animation-options';
import type { AnimationOptions } from './types';
import { convertEasesOptions } from './utils/eases';
import { setStyles } from './utils/set-styles';
import {
  convertTransformKeyframes,
  removeOptionOverrides,
} from './utils/transforms';
import { waapiFallback } from './waapi-fallback';

// Fallback to immediate style application if Waapi is not available
if (canUseDOM()) {
  if (!Element.prototype.animate) {
    Element.prototype.animate = waapiFallback as any;
  }
}

type ExtendedAnimation = Animation & {
  readonly complete?: Promise<Animation>;
};

/**
 * A small wrapper around Waapi which supports things like preset eases,
 * transform shortahnds, and a promise based "completion" api.
 *
 * @param element HtmlElement
 * @param keyframes animation keyframe defintion
 * @param options animation options (duration, easing, etc.);
 * @returns Waapi animation object
 *
 * @example
 * animateElement(
 *  document.querySelector("#my-element"),
 *  { x: 100 },
 *  { duration: 1000 }
 * ).complete.then(() => {
 *  console.log('animation fully finished')
 * });
 */

export function animateElement(
  element: HTMLElement,
  keyframes: AnimationKeyframeWithOptions,
  options: AnimationOptions
): ExtendedAnimation {
  let cancelled = false;

  // convert our keyframe format to a waapi compatible format
  const frames = convertTransformKeyframes(removeOptionOverrides(keyframes));

  // convert our options to a waapi compatible format
  const opts = convertEasesOptions(applyDefaultOptions(options));

  // start our animation
  // @ts-expect-error FIXME(strictNullChecks)
  const animation = element.animate(frames, opts);

  // a complete promise, which differs from finished, insofar
  // as it's only called when the animation is not cancelled
  // or interrupted.
  const complete = new Promise((resolve, reject) => {
    animation.onfinish = () => {
      if (cancelled) return reject();
      return resolve(animation);
    };
  });

  // Instead of leveraging fill (which can cause memory leaks), we
  // manually set the styles once the animation has finished.
  complete
    .then(() => {
      setStyles(element, keyframes);
    })
    .catch(noop);

  // Add a proxy, extending the base waapi animation object
  return new Proxy(animation, {
    get(target, property) {
      if (property === 'complete') {
        return complete;
      }

      if (property === 'stop') {
        return () => {
          cancelled = true;
          if (animation.playState !== 'finished') {
            (animation as any).commitStyles();
          }

          animation.cancel();
        };
      }

      // @ts-expect-error FIXME(noImplicitAny)
      return target[property];
    },
  });
}
